import { useEffect, useId, useMemo, useRef, useState } from 'react';
import {
  filterOptions, summarise, toggleValue, type MultiSelectOption,
} from './multi-select-model';

export type { MultiSelectOption };

/**
 * CR-46 — a real listbox, in its multiple flavour.
 *
 * This replaces `<select multiple>`, which the product editor had reached for on
 * eight axes. A native multiple select is the wrong control here and the hint
 * under every one of them admitted it: *"Hold Ctrl or Cmd to choose more than
 * one."* That instruction is the tell. It cannot be operated on a touch screen
 * at all, one unmodified click silently destroys the entire selection, and at
 * `size={4}` it clipped its own list mid-option — the purity box showed
 * "10k 14k 18k 21k" with 22k sliced in half.
 *
 * The prototype never drew one: `admin-console.html` contains zero
 * `select multiple`, and CR-46 records that the multi-select was added to the
 * component library properly rather than bolted onto one screen. This is that
 * control, ported — the panel stays open while you pick, the trigger carries the
 * summary, and past four selections a count says more than the list does.
 *
 * The decisions live in `multi-select-model.ts` and are unit-tested there. What
 * is here is the DOM: focus, keyboard, and when the panel opens and shuts.
 */
export function MultiSelect(
  { options, value, onChange, disabled, placeholder = 'Choose…', searchAfter = 8, label }: {
    options: readonly MultiSelectOption[];
    value: readonly string[];
    onChange: (next: string[]) => void;
    disabled?: boolean;
    placeholder?: string;
    /** Longer than this and the panel gains a search box, as the prototype does. */
    searchAfter?: number;
    /** Announced on the trigger. The visible `<Field>` label is not associated
     *  with a button the way it is with an input, so a screen reader needs it. */
    label?: string;
  },
) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const root = useRef<HTMLDivElement>(null);
  const listId = useId();

  /*
   * Listened for only while the panel is open. A product editor renders eight of
   * these and a page carrying eight idle document listeners is eight chances to
   * leak one.
   */
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!root.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      setOpen(false);
      // Focus goes back to the trigger, or Escape strands a keyboard user at
      // the top of the document.
      root.current?.querySelector('button')?.focus();
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Clearing the search on close, so reopening never starts mid-filter showing
  // a list that looks short for no visible reason.
  useEffect(() => { if (!open) setQ(''); }, [open]);

  const chosen = useMemo(() => new Set(value), [value]);
  const summary = useMemo(() => summarise(options, value), [options, value]);
  const shown = useMemo(() => filterOptions(options, q), [options, q]);

  const toggle = (v: string) => onChange(toggleValue(options, value, v));

  return (
    <div className="mk-ms" ref={root}>
      <button
        type="button"
        className="mk-ms-t"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-label={label}
        onClick={(e) => { e.preventDefault(); setOpen((o) => !o); }}
        onKeyDown={(e) => {
          // Down arrow opens without toggling, which is what a listbox trigger
          // does everywhere else; Enter and Space are the button's own.
          if (e.key === 'ArrowDown') { e.preventDefault(); setOpen(true); }
        }}
      >
        <span className={summary ? 'mk-ms-v' : 'mk-ms-v isph'}>{summary ?? placeholder}</span>
        <svg className="mk-ms-c" width="9" height="6" viewBox="0 0 9 6" aria-hidden="true">
          <path d="M1 1l3.5 3.5L8 1" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
        </svg>
      </button>

      {open ? (
        <div className="mk-ms-p" id={listId} role="listbox" aria-multiselectable="true">
          {options.length > searchAfter ? (
            <input
              className="mk-ms-s"
              type="search"
              autoFocus
              value={q}
              placeholder="Search"
              aria-label="Search options"
              onChange={(e) => setQ(e.target.value)}
              onClick={(e) => e.stopPropagation()}
            />
          ) : null}
          <div className="mk-ms-l">
            {shown.length === 0 ? (
              <div className="mk-ms-none">Nothing matches</div>
            ) : shown.map((o) => {
              const on = chosen.has(o.v);
              return (
                <div
                  key={o.v}
                  role="option"
                  aria-selected={on}
                  tabIndex={0}
                  className={on ? 'mk-ms-o on' : 'mk-ms-o'}
                  /**
                   * `preventDefault` is load-bearing, and the panel shut after
                   * every single pick without it.
                   *
                   * `Field` renders a `<label>`, and a label's activation
                   * behaviour forwards a click on any of its content to the
                   * control it labels — here, the trigger button. So choosing an
                   * option toggled the value AND re-clicked the trigger, which
                   * closed the panel. Cancelling the click cancels that
                   * forwarding; `stopPropagation` keeps the label from seeing it
                   * at all.
                   *
                   * The deeper fix is that a `<label>` should not wrap a
                   * listbox — a label names one labelable control and this is
                   * not one. That belongs to `Field` in `ui/index.tsx`, which is
                   * shared; this is the contained fix.
                   */
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggle(o.v); }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(o.v); }
                  }}
                >
                  {/* A box rather than a tick alone: an unchosen option has to
                      look like something you can choose, not like blank space. */}
                  <span className="mk-ms-bx" aria-hidden="true">{on ? '✓' : ''}</span>
                  <span>{o.label}</span>
                </div>
              );
            })}
          </div>
        </div>
      ) : null}
    </div>
  );
}
